<template>
  <v-card max-width="850" flat>
    <div>
      <v-list three-line>
        <div v-if="activePromotion && activePromotion.isApplicable">
          <thg-promotion-list-item :value="activePromotion" />
        </div>

        <v-list-item v-for="(year, i) in years" :key="i">
          <v-list-item-avatar tile size="60">
            <v-img :src="src" style="border-radius: 4px" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title v-if="price(year).title" v-html="price(year).title" />
            <v-list-item-subtitle v-if="vehicleClass">
              <v-icon small>mdi-check</v-icon>
              {{ $t(vehicleClass.title) + " " + $t(vehicleClass.classSpeaking) }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="price(year).subtitle" v-html="price(year).subtitle" />
            <v-list-item-subtitle v-if="price(year).description" v-html="price(year).description" />
            <v-list-item-subtitle v-if="price(year).hint" v-html="price(year).hint" />
          </v-list-item-content>
          <v-list-item-action>
            <v-btn class="text-right" v-if="!hideEdit" text x-small color="info" @click="goToYearSelection">
              {{ $t("edit") }}
            </v-btn>
            <span>{{ $t(price(year).prefix) }}<br /></span>
            <span>{{ price(year).totalAmountAsString }}</span>
          </v-list-item-action>
        </v-list-item>

        <v-divider v-if="showImpact" inset> </v-divider>
        <v-list-item v-if="showImpact">
          <v-list-item-avatar tile size="60">
            <v-img :src="impactType.src" style="border-radius: 4px" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              {{ $t("report.thgCheckoutOrderCard.sustainable", { value: $t(impactType.title) }) }}
            </v-list-item-title>
            <v-list-item-subtitle>
              <v-icon small>mdi-check</v-icon>
              {{ $t(impactType.subtitleAlt) }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              <v-icon small>mdi-check</v-icon>
              {{ $t("report.thgCheckoutOrderCard.paymentReductionHint") }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn class="text-right" v-if="!hideEdit" text x-small color="info" @click="goToImpactSelector">
              {{ $t("edit") }}
            </v-btn>
            <span>{{ $t("report.thgCheckoutOrderCard.reduction") }}</span>
            <span>{{ impact }}</span>
          </v-list-item-action>
        </v-list-item>
        <v-divider v-if="showImpact" inset> </v-divider>
      </v-list>
    </div>
  </v-card>
</template>

<script lang="ts">
import ImageDialog from "@/components/utility/ImageDialog.vue";
import { PartnerColor } from "@/lib/partnerColor";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { PartnerActiveOfferModule } from "@/store/modules/partner-active-config.store";
import { ReportModule } from "@/store/modules/report.store";
import { ThgCreateModule } from "@/store/modules/thg.create.store";
import { ImpactTypeEnum } from "@/views/thg/enum/impact-type.enum";
import { IThgImpact } from "@/views/thg/interface/ThgImpact.interface";
import { Component, Prop } from "vue-property-decorator";
import NumberplateForm from "../utility/NumberplateForm.vue";
import { ThgVehicleTypes } from "@/store/enum/thg/thg-vehicle-types.enum";
import ThgCheckoutCard from "@/views/thg/ThgCheckoutCard.vue";
import ThgPromotionListItem from "./ThgPromotionListItem.vue";
import { mixins } from "vue-class-component";
import ThgImpactSelectorScreensMixin from "./mixins/ThgImpactSelectorScreensMixin.vue";
import ThgYearSelectionScreensMixin from "./mixins/ThgYearSelectionScreensMixin.vue";

@Component({
  components: {
    ImageDialog,
    NumberplateForm,
    ThgCheckoutCard,
    ThgPromotionListItem
  }
})
export default class ThgCheckoutOrderCard extends mixins(ThgImpactSelectorScreensMixin, ThgYearSelectionScreensMixin) {
  @Prop({ default: false })
  hideEdit?: boolean;

  VehicleTypes = ThgVehicleTypes;

  price(year: number) {
    return PartnerActiveOfferModule.priceConfig.get({
      year: year,
      vehicleClass: ThgCreateModule.vehicleClass || ThgVehicleTypes.M1
    });
  }

  get activePromotion() {
    return PartnerActiveOfferModule.currentSignUpPromotion;
  }

  get countryCode() {
    return ReportModule?.partner?.countryCode;
  }

  partnerColor = new PartnerColor();

  get years(): number[] {
    return ThgCreateModule.years;
  }

  get showImpact(): number {
    if (ThgCreateModule.impactType === ImpactTypeEnum.customerAccount) {
      return 0;
    }
    return ThgCreateModule.impactFactor;
  }

  showValueOrDefault(value: string, title: string) {
    if (!value.replace(" ", "")) {
      return this.$t("components.thg.ThgCreateOverviewCard.defaultValue", { title: this.$t(title) });
    }

    return value;
  }

  goToImpactSelector() {
    const screenName = this.impactSelectorScreens.find((screen) => ReportModule.screenOder.includes(screen));

    if (screenName) {
      this.goToScreen(screenName);
    } else {
      this.$toast.error(this.$t("components.thg.ThgCreateOverviewCard.errors.impactSelector"));
      this.$log.error(this.$t("components.thg.ThgCreateOverviewCard.errors.impactSelector"));
    }
  }

  goToYearSelection() {
    const screenName = this.yearSelectionScreens.find((screen) => ReportModule.screenOder.includes(screen));

    if (screenName) {
      this.goToScreen(screenName);
    } else {
      this.$toast.error(this.$t("components.thg.ThgCreateOverviewCard.errors.yearSelection"));
      this.$log.error(this.$t("components.thg.ThgCreateOverviewCard.errors.yearSelection"));
    }
  }

  goToScreen(screenName: ReportScreenEnum) {
    ReportModule.setStep(screenName);
    this.$router.push({ name: screenName });
  }

  get impact(): string {
    return ThgCreateModule.impactFactor + " %";
  }

  get vehicleClass() {
    if (ThgCreateModule.vehicleClass) {
      const vehicle = PartnerActiveOfferModule.priceConfig.vehicleClassInformation(ThgCreateModule.vehicleClass);
      if (vehicle) {
        return vehicle;
      }
    }
    return undefined;
  }

  get src() {
    if (this.vehicleClass) {
      return this.vehicleClass.src;
    }

    return "https://static.mmmint.ai/wirkaufendeinethg/photo-1580048915913-4f8f5cb481c4-1.jpeg";
  }

  get impactType(): IThgImpact {
    return ThgCreateModule.impactTypeItem;
  }
}
</script>

<style>
.form {
  margin-top: 20px;
}
</style>
